import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  MessageSquare,
  Send,
  Users,
  Phone,
  Radio,
  CheckCircle,
  XCircle,
  ArrowUpDown,
  Loader2,
  GraduationCap,
  Eye,
  BookOpen,
  CalendarClock,
  Smartphone,
  RefreshCw,
  BarChart3,
} from "lucide-react";

interface WhatsappMessage {
  id: string;
  direction: "inbound" | "outbound";
  recipientPhone: string;
  messageBody: string | null;
  status: string;
  messageType: string;
  createdAt: string;
}

interface WhatsAppStatus {
  configured: boolean;
  messageCount: number;
}

interface BroadcastResult {
  total: number;
  sent: number;
  failed: number;
  errors: string[];
}

interface StudentWithParent {
  id: string;
  firstName: string;
  lastName: string;
  grade: number;
  parentPhone: string;
  subscriptionStatus: string;
}

interface DailyProgressPreview {
  message: string;
  phone: string;
  student: string;
  grade: number;
  total: number;
  correct: number;
  accuracy: number;
  todayTotal: number;
  todayCorrect: number;
}

interface WeeklyOptInParent {
  userId: string;
  studentName: string;
  grade: number;
  parentName: string;
  parentPhone: string;
  lastReportSent: string | null;
  subscriptionStatus: string;
}

interface WeeklyOverview {
  optedInCount: number;
  recentBatches: WeeklyBatchLog[];
}

interface WeeklyBatchLog {
  id: string;
  triggeredBy: string;
  totalParents: number;
  sent: number;
  failed: number;
  skipped: number;
  errors: string[];
  startedAt: string;
  completedAt: string | null;
}

interface WeeklyPreview {
  message: string;
  parentPhone: string;
  parentName: string;
}

const statusVariant: Record<string, "default" | "outline" | "destructive" | "secondary"> = {
  sent: "default",
  delivered: "outline",
  failed: "destructive",
  received: "secondary",
  read: "outline",
};

const directionVariant: Record<string, "default" | "secondary"> = {
  inbound: "secondary",
  outbound: "default",
};

export default function WhatsAppManager() {
  const { toast } = useToast();
  const [sendPhone, setSendPhone] = useState("");
  const [sendMessage, setSendMessage] = useState("");
  const [broadcastMessage, setBroadcastMessage] = useState("");
  const [broadcastGrade, setBroadcastGrade] = useState<string>("");
  const [broadcastStatus, setBroadcastStatus] = useState<string>("");
  const [directionFilter, setDirectionFilter] = useState<string>("all");
  const [broadcastResult, setBroadcastResult] = useState<BroadcastResult | null>(null);
  const [selectedStudentId, setSelectedStudentId] = useState<string>("");
  const [studentSearch, setStudentSearch] = useState("");
  const [useSimulate, setUseSimulate] = useState(false);
  const [simCount, setSimCount] = useState("15");
  const [simCorrect, setSimCorrect] = useState("11");
  const [progressPreview, setProgressPreview] = useState<DailyProgressPreview | null>(null);
  const [weeklyPreviewUserId, setWeeklyPreviewUserId] = useState("");
  const [weeklyPreview, setWeeklyPreview] = useState<WeeklyPreview | null>(null);
  const [weeklyPreviewOpen, setWeeklyPreviewOpen] = useState(false);
  const [weeklyBatchResult, setWeeklyBatchResult] = useState<WeeklyBatchLog | null>(null);
  const [testPhone, setTestPhone] = useState("");
  const [testUserId, setTestUserId] = useState("");
  const [testSendResult, setTestSendResult] = useState<{ sentTo: string; message: string } | null>(null);

  const { data: status, isLoading: statusLoading } = useQuery<WhatsAppStatus>({
    queryKey: ["/api/admin/whatsapp/status"],
  });

  const { data: messagesData, isLoading: messagesLoading } = useQuery<{
    messages: WhatsappMessage[];
    total: number;
  }>({
    queryKey: ["/api/admin/whatsapp/messages"],
  });

  const sendMutation = useMutation({
    mutationFn: async (body: { phone: string; message: string }) => {
      const res = await apiRequest("POST", "/api/admin/whatsapp/send", body);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/whatsapp/messages"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/whatsapp/status"] });
      toast({ title: "Message sent successfully" });
      setSendPhone("");
      setSendMessage("");
    },
    onError: (error: Error) => {
      toast({ title: "Failed to send message", description: error.message, variant: "destructive" });
    },
  });

  const broadcastMutation = useMutation({
    mutationFn: async (body: { message: string; grade?: number; subscriptionStatus?: string }) => {
      const res = await apiRequest("POST", "/api/admin/whatsapp/broadcast", body);
      return res.json();
    },
    onSuccess: (data: BroadcastResult) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/whatsapp/messages"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/whatsapp/status"] });
      setBroadcastResult(data);
      toast({ title: `Broadcast complete: ${data.sent}/${data.total} sent` });
      setBroadcastMessage("");
      setBroadcastGrade("");
      setBroadcastStatus("");
    },
    onError: (error: Error) => {
      toast({ title: "Broadcast failed", description: error.message, variant: "destructive" });
    },
  });

  const { data: studentsWithParents, isLoading: studentsLoading } = useQuery<StudentWithParent[]>({
    queryKey: ["/api/admin/whatsapp/students-with-parents"],
  });

  const { data: weeklyOverview, isLoading: weeklyOverviewLoading, refetch: refetchOverview } = useQuery<WeeklyOverview>({
    queryKey: ["/api/admin/whatsapp-reports/overview"],
  });

  const { data: weeklyOptedIn, isLoading: weeklyOptedInLoading, refetch: refetchOptedIn } = useQuery<WeeklyOptInParent[]>({
    queryKey: ["/api/admin/whatsapp-reports/opted-in-parents"],
  });

  const weeklyRunBatchMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/admin/whatsapp-reports/run-batch", {});
      return res.json();
    },
    onSuccess: (data: any) => {
      setWeeklyBatchResult(data);
      refetchOverview();
      refetchOptedIn();
      toast({ title: `Batch complete: ${data.sent} sent, ${data.failed} failed` });
    },
    onError: (error: Error) => {
      toast({ title: "Batch failed", description: error.message, variant: "destructive" });
    },
  });

  const weeklySendOneMutation = useMutation({
    mutationFn: async (userId: string) => {
      const res = await apiRequest("POST", `/api/admin/whatsapp-reports/send/${userId}`, {});
      return res.json();
    },
    onSuccess: (data: any) => {
      refetchOptedIn();
      if (data.skipped) {
        toast({ title: "Skipped", description: "Parent is not opted in" });
      } else if (data.success) {
        toast({ title: "Report sent successfully" });
      } else {
        toast({ title: "Send failed", description: data.error, variant: "destructive" });
      }
    },
    onError: (error: Error) => {
      toast({ title: "Failed to send", description: error.message, variant: "destructive" });
    },
  });

  const weeklyPreviewMutation = useMutation({
    mutationFn: async ({ userId }: { userId: string }) => {
      const res = await apiRequest("GET", `/api/admin/whatsapp-reports/preview/${userId}`);
      return res.json();
    },
    onSuccess: (data: WeeklyPreview) => {
      setWeeklyPreview(data);
      setWeeklyPreviewOpen(true);
    },
    onError: (error: Error) => {
      toast({ title: "Preview failed", description: error.message, variant: "destructive" });
    },
  });

  const weeklyToggleOptInMutation = useMutation({
    mutationFn: async ({ userId, optIn }: { userId: string; optIn: boolean }) => {
      const res = await apiRequest("PATCH", `/api/admin/whatsapp-reports/opt-in/${userId}`, { optIn });
      return res.json();
    },
    onSuccess: () => {
      refetchOptedIn();
      refetchOverview();
      toast({ title: "Opt-in status updated" });
    },
    onError: (error: Error) => {
      toast({ title: "Failed to update opt-in", description: error.message, variant: "destructive" });
    },
  });

  const testSendMutation = useMutation({
    mutationFn: async ({ phone, userId }: { phone: string; userId?: string }) => {
      const res = await apiRequest("POST", "/api/admin/whatsapp-reports/test-send", { phone, userId: userId || undefined });
      return res.json();
    },
    onSuccess: (data: { sentTo: string; message: string }) => {
      setTestSendResult(data);
      toast({ title: "Test message sent", description: `Delivered to ${data.sentTo}` });
    },
    onError: (error: Error) => {
      toast({ title: "Test send failed", description: error.message, variant: "destructive" });
    },
  });

  const previewMutation = useMutation({
    mutationFn: async (body: { userId: string; simulateCount?: number; simulateCorrect?: number }) => {
      const res = await apiRequest("POST", "/api/admin/whatsapp/preview-daily-progress", body);
      return res.json();
    },
    onSuccess: (data: DailyProgressPreview) => {
      setProgressPreview(data);
    },
    onError: (error: Error) => {
      toast({ title: "Preview failed", description: error.message, variant: "destructive" });
    },
  });

  const sendProgressMutation = useMutation({
    mutationFn: async (body: { userId: string; simulateCount?: number; simulateCorrect?: number }) => {
      const res = await apiRequest("POST", "/api/admin/whatsapp/send-daily-progress", body);
      return res.json();
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/whatsapp/messages"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/whatsapp/status"] });
      toast({ title: "Daily progress sent", description: `Message delivered to ${data.phone}` });
      setProgressPreview(null);
    },
    onError: (error: Error) => {
      toast({ title: "Failed to send", description: error.message, variant: "destructive" });
    },
  });

  const filteredStudents = studentsWithParents?.filter(s => {
    if (!studentSearch.trim()) return true;
    const q = studentSearch.toLowerCase();
    return s.firstName.toLowerCase().includes(q) || s.lastName.toLowerCase().includes(q) || s.parentPhone.includes(q);
  }) || [];

  const handlePreview = () => {
    if (!selectedStudentId) return;
    const body: any = { userId: selectedStudentId };
    if (useSimulate) {
      body.simulateCount = parseInt(simCount) || 15;
      body.simulateCorrect = parseInt(simCorrect) || 11;
    }
    previewMutation.mutate(body);
  };

  const handleSendProgress = () => {
    if (!selectedStudentId) return;
    const body: any = { userId: selectedStudentId };
    if (useSimulate) {
      body.simulateCount = parseInt(simCount) || 15;
      body.simulateCorrect = parseInt(simCorrect) || 11;
    }
    sendProgressMutation.mutate(body);
  };

  const handleSend = () => {
    if (!sendPhone.trim() || !sendMessage.trim()) return;
    sendMutation.mutate({ phone: sendPhone.trim(), message: sendMessage.trim() });
  };

  const handleBroadcast = () => {
    if (!broadcastMessage.trim()) return;
    const body: { message: string; grade?: number; subscriptionStatus?: string } = {
      message: broadcastMessage.trim(),
    };
    if (broadcastGrade && broadcastGrade !== "all") body.grade = parseInt(broadcastGrade);
    if (broadcastStatus && broadcastStatus !== "all") body.subscriptionStatus = broadcastStatus;
    broadcastMutation.mutate(body);
  };

  const filteredMessages = messagesData?.messages?.filter((m) => {
    if (directionFilter === "all") return true;
    return m.direction === directionFilter;
  }) || [];

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-3 flex-wrap">
        <MessageSquare className="h-6 w-6" />
        <h1 className="text-2xl font-bold">WhatsApp Manager</h1>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Radio className="h-5 w-5" />
            WhatsApp Status
          </CardTitle>
          <CardDescription>Connection status and message statistics</CardDescription>
        </CardHeader>
        <CardContent>
          {statusLoading ? (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading status...
            </div>
          ) : status ? (
            <div className="flex items-center gap-6 flex-wrap">
              <div className="flex items-center gap-2">
                {status.configured ? (
                  <CheckCircle className="h-5 w-5 text-green-600" />
                ) : (
                  <XCircle className="h-5 w-5 text-destructive" />
                )}
                <span className="text-sm font-medium">
                  {status.configured ? "Configured" : "Not Configured"}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <MessageSquare className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">
                  {status.messageCount} total messages
                </span>
              </div>
            </div>
          ) : null}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Send className="h-5 w-5" />
              Send Message
            </CardTitle>
            <CardDescription>Send a WhatsApp message to a single phone number</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="send-phone">Phone Number</Label>
              <div className="flex items-center gap-2">
                <Phone className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                <Input
                  id="send-phone"
                  placeholder="+1876XXXXXXX"
                  value={sendPhone}
                  onChange={(e) => setSendPhone(e.target.value)}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="send-message">Message</Label>
              <Textarea
                id="send-message"
                placeholder="Type your message..."
                value={sendMessage}
                onChange={(e) => setSendMessage(e.target.value)}
                rows={4}
              />
            </div>
            <Button
              onClick={handleSend}
              disabled={sendMutation.isPending || !sendPhone.trim() || !sendMessage.trim()}
            >
              {sendMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              Send Message
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Broadcast Message
            </CardTitle>
            <CardDescription>Send a message to multiple users with optional filters</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="broadcast-message">Message</Label>
              <Textarea
                id="broadcast-message"
                placeholder="Type your broadcast message..."
                value={broadcastMessage}
                onChange={(e) => setBroadcastMessage(e.target.value)}
                rows={4}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Grade Filter</Label>
                <Select value={broadcastGrade} onValueChange={setBroadcastGrade}>
                  <SelectTrigger>
                    <SelectValue placeholder="All grades" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All grades</SelectItem>
                    <SelectItem value="4">Grade 4</SelectItem>
                    <SelectItem value="5">Grade 5</SelectItem>
                    <SelectItem value="6">Grade 6</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Subscription Status</Label>
                <Select value={broadcastStatus} onValueChange={setBroadcastStatus}>
                  <SelectTrigger>
                    <SelectValue placeholder="All statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All statuses</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="trial">Trial</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button
              onClick={handleBroadcast}
              disabled={broadcastMutation.isPending || !broadcastMessage.trim()}
            >
              {broadcastMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Users className="h-4 w-4 mr-2" />
              )}
              Send Broadcast
            </Button>

            {broadcastResult && (
              <div className="rounded-md border p-4 space-y-2">
                <p className="text-sm font-medium">Broadcast Results</p>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground">Total:</span>
                    <span className="font-medium">{broadcastResult.total}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-3 w-3 text-green-600" />
                    <span className="text-muted-foreground">Sent:</span>
                    <span className="font-medium">{broadcastResult.sent}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <XCircle className="h-3 w-3 text-destructive" />
                    <span className="text-muted-foreground">Failed:</span>
                    <span className="font-medium">{broadcastResult.failed}</span>
                  </div>
                </div>
                {broadcastResult.errors.length > 0 && (
                  <div className="text-xs text-destructive space-y-1 mt-2">
                    {broadcastResult.errors.map((err, i) => (
                      <p key={i}>{err}</p>
                    ))}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BookOpen className="h-5 w-5" />
            Daily Progress Notification
          </CardTitle>
          <CardDescription>Send AI-powered curriculum progress updates to a student's parent via WhatsApp. The message is generated using real Quizzative curriculum data.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Search Student</Label>
            <Input
              placeholder="Search by name or phone..."
              value={studentSearch}
              onChange={(e) => setStudentSearch(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label>Select Student</Label>
            {studentsLoading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading students...
              </div>
            ) : filteredStudents.length === 0 ? (
              <p className="text-sm text-muted-foreground">No students with parent phone numbers found.</p>
            ) : (
              <Select value={selectedStudentId} onValueChange={(val) => { setSelectedStudentId(val); setProgressPreview(null); }}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a student..." />
                </SelectTrigger>
                <SelectContent>
                  {filteredStudents.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      <span className="flex items-center gap-2">
                        <GraduationCap className="h-3 w-3" />
                        {s.firstName} {s.lastName} - Grade {s.grade} ({s.parentPhone})
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <Checkbox
                id="simulate-toggle"
                checked={useSimulate}
                onCheckedChange={(checked) => setUseSimulate(checked === true)}
              />
              <Label htmlFor="simulate-toggle" className="text-sm cursor-pointer">
                Simulate question counts
              </Label>
            </div>
            {useSimulate && (
              <div className="flex items-center gap-2 flex-wrap">
                <div className="flex items-center gap-1">
                  <Label className="text-xs text-muted-foreground whitespace-nowrap">Total:</Label>
                  <Input
                    className="w-20"
                    type="number"
                    value={simCount}
                    onChange={(e) => setSimCount(e.target.value)}
                  />
                </div>
                <div className="flex items-center gap-1">
                  <Label className="text-xs text-muted-foreground whitespace-nowrap">Correct:</Label>
                  <Input
                    className="w-20"
                    type="number"
                    value={simCorrect}
                    onChange={(e) => setSimCorrect(e.target.value)}
                  />
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <Button
              onClick={handlePreview}
              disabled={!selectedStudentId || previewMutation.isPending}
              variant="outline"
            >
              {previewMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Eye className="h-4 w-4 mr-2" />
              )}
              Preview Message
            </Button>
            <Button
              onClick={handleSendProgress}
              disabled={!selectedStudentId || sendProgressMutation.isPending}
            >
              {sendProgressMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              Send to Parent
            </Button>
          </div>

          {progressPreview && (
            <div className="rounded-md border p-4 space-y-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <p className="text-sm font-medium">Message Preview</p>
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge variant="outline">{progressPreview.student}</Badge>
                  <Badge variant="secondary">Grade {progressPreview.grade}</Badge>
                  <Badge variant="secondary">{progressPreview.accuracy}% accuracy</Badge>
                </div>
              </div>
              <div className="text-sm text-muted-foreground">
                <span className="font-medium">To: </span>
                <span className="font-mono">{progressPreview.phone}</span>
                {" | "}
                <span className="font-medium">Today's actual: </span>
                {progressPreview.todayTotal} questions, {progressPreview.todayCorrect} correct
                {" | "}
                <span className="font-medium">Using: </span>
                {progressPreview.total} questions, {progressPreview.correct} correct
              </div>
              <div className="bg-muted/50 rounded-md p-3 text-sm whitespace-pre-wrap">
                {progressPreview.message}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <CardTitle className="flex items-center gap-2">
                <ArrowUpDown className="h-5 w-5" />
                Message Log
              </CardTitle>
              <CardDescription>
                Recent WhatsApp messages{messagesData ? ` (${messagesData.total} total)` : ""}
              </CardDescription>
            </div>
            <Select value={directionFilter} onValueChange={setDirectionFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="inbound">Inbound</SelectItem>
                <SelectItem value="outbound">Outbound</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {messagesLoading ? (
            <div className="flex items-center gap-2 py-8 justify-center text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading messages...
            </div>
          ) : filteredMessages.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>No messages found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-2 font-medium text-muted-foreground">Direction</th>
                    <th className="text-left py-2 px-2 font-medium text-muted-foreground">Phone</th>
                    <th className="text-left py-2 px-2 font-medium text-muted-foreground">Message</th>
                    <th className="text-left py-2 px-2 font-medium text-muted-foreground">Status</th>
                    <th className="text-left py-2 px-2 font-medium text-muted-foreground">Type</th>
                    <th className="text-left py-2 px-2 font-medium text-muted-foreground">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredMessages.map((msg) => (
                    <tr key={msg.id} className="border-b last:border-b-0">
                      <td className="py-2 px-2">
                        <Badge variant={directionVariant[msg.direction] || "default"}>
                          {msg.direction}
                        </Badge>
                      </td>
                      <td className="py-2 px-2 font-mono text-xs">{msg.recipientPhone}</td>
                      <td className="py-2 px-2 max-w-[200px] truncate" title={msg.messageBody || ""}>
                        {msg.messageBody || "–"}
                      </td>
                      <td className="py-2 px-2">
                        <Badge variant={statusVariant[msg.status] || "default"}>
                          {msg.status}
                        </Badge>
                      </td>
                      <td className="py-2 px-2 text-muted-foreground">{msg.messageType}</td>
                      <td className="py-2 px-2 text-muted-foreground whitespace-nowrap">
                        {new Date(msg.createdAt).toLocaleDateString("en-JM", {
                          timeZone: "America/Jamaica",
                          month: "short",
                          day: "numeric",
                          hour: "numeric",
                          minute: "2-digit",
                          hour12: true,
                        })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
      {/* ── Weekly Parent Reports ─────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <CalendarClock className="w-5 h-5 text-primary" />
              <div>
                <CardTitle>Weekly WhatsApp Reports</CardTitle>
                <CardDescription>Automatic Sunday 6pm (Jamaica time) progress updates to opted-in parents</CardDescription>
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button
                variant="outline"
                size="sm"
                onClick={() => { refetchOverview(); refetchOptedIn(); }}
              >
                <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
                Refresh
              </Button>
              <Button
                size="sm"
                onClick={() => weeklyRunBatchMutation.mutate()}
                disabled={weeklyRunBatchMutation.isPending}
              >
                {weeklyRunBatchMutation.isPending ? (
                  <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                ) : (
                  <Send className="w-3.5 h-3.5 mr-1.5" />
                )}
                Run Batch Now
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="rounded-md border p-3 space-y-1">
              <p className="text-xs text-muted-foreground">Opted-In Parents</p>
              <p className="text-2xl font-bold">
                {weeklyOverviewLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : (weeklyOverview?.optedInCount ?? 0)}
              </p>
            </div>
            {weeklyOverview?.recentBatches?.[0] && (
              <>
                <div className="rounded-md border p-3 space-y-1">
                  <p className="text-xs text-muted-foreground">Last Batch Sent</p>
                  <p className="text-2xl font-bold text-green-600">{weeklyOverview.recentBatches[0].sent}</p>
                </div>
                <div className="rounded-md border p-3 space-y-1">
                  <p className="text-xs text-muted-foreground">Last Batch Failed</p>
                  <p className="text-2xl font-bold text-destructive">{weeklyOverview.recentBatches[0].failed}</p>
                </div>
                <div className="rounded-md border p-3 space-y-1">
                  <p className="text-xs text-muted-foreground">Last Run</p>
                  <p className="text-sm font-medium">
                    {new Date(weeklyOverview.recentBatches[0].startedAt).toLocaleDateString("en-JM", {
                      timeZone: "America/Jamaica", month: "short", day: "numeric", hour: "numeric", minute: "2-digit", hour12: true
                    })}
                  </p>
                </div>
              </>
            )}
          </div>

          {weeklyBatchResult && (
            <div className="rounded-md border p-4 bg-muted/40 space-y-2">
              <p className="text-sm font-medium flex items-center gap-2">
                <BarChart3 className="w-4 h-4 text-primary" />
                Last Manual Run Results
              </p>
              <div className="flex gap-4 text-sm">
                <span className="text-green-600 font-medium">{weeklyBatchResult.sent} sent</span>
                <span className="text-destructive font-medium">{weeklyBatchResult.failed} failed</span>
                <span className="text-muted-foreground">{weeklyBatchResult.skipped} skipped</span>
                <span className="text-muted-foreground">of {weeklyBatchResult.totalParents} opted-in</span>
              </div>
              {Array.isArray(weeklyBatchResult.errors) && weeklyBatchResult.errors.length > 0 && (
                <div className="space-y-1">
                  {weeklyBatchResult.errors.slice(0, 5).map((e, i) => (
                    <p key={i} className="text-xs text-destructive">{e}</p>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Test send panel */}
          <div className="rounded-md border p-4 space-y-3">
            <p className="text-sm font-medium flex items-center gap-2">
              <Send className="w-4 h-4 text-primary" />
              Send a Test Message
            </p>
            <p className="text-xs text-muted-foreground">
              Verify that Twilio is connected and the message format looks right. Send to any WhatsApp number.
            </p>
            <div className="flex flex-wrap gap-2">
              <Input
                placeholder="WhatsApp number, e.g. +18761234567"
                value={testPhone}
                onChange={e => { setTestPhone(e.target.value); setTestSendResult(null); }}
                className="max-w-xs"
              />
              <select
                className="border rounded-md px-3 py-1.5 text-sm bg-background"
                value={testUserId}
                onChange={e => setTestUserId(e.target.value)}
              >
                <option value="">Generic test message</option>
                {studentsWithParents?.map(s => (
                  <option key={s.id} value={s.id}>{s.firstName} {s.lastName} (Gr {s.grade})</option>
                ))}
              </select>
              <Button
                size="sm"
                disabled={!testPhone.trim() || testSendMutation.isPending}
                onClick={() => testSendMutation.mutate({ phone: testPhone.trim(), userId: testUserId || undefined })}
              >
                {testSendMutation.isPending ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1.5" />}
                Send Test
              </Button>
            </div>
            {testSendResult && (
              <div className="space-y-2">
                <p className="text-xs text-green-600 font-medium">Sent to {testSendResult.sentTo}</p>
                <div className="rounded-md bg-muted/60 p-3 text-xs whitespace-pre-line font-mono leading-relaxed max-h-48 overflow-y-auto">
                  {testSendResult.message}
                </div>
              </div>
            )}
          </div>

          {/* Opted-in parent list */}
          <div className="space-y-2">
            <p className="text-sm font-medium">Opted-In Parents ({weeklyOptedIn?.length ?? 0})</p>
            {weeklyOptedInLoading ? (
              <div className="flex items-center gap-2 py-4 text-muted-foreground text-sm">
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading...
              </div>
            ) : !weeklyOptedIn || weeklyOptedIn.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">No parents are currently opted in.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 px-2 font-medium text-muted-foreground">Student</th>
                      <th className="text-left py-2 px-2 font-medium text-muted-foreground">Parent</th>
                      <th className="text-left py-2 px-2 font-medium text-muted-foreground">Phone</th>
                      <th className="text-left py-2 px-2 font-medium text-muted-foreground">Last Sent</th>
                      <th className="text-left py-2 px-2 font-medium text-muted-foreground">Opt-In</th>
                      <th className="text-left py-2 px-2 font-medium text-muted-foreground">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {weeklyOptedIn.map((row) => (
                      <tr key={row.userId} className="border-b last:border-b-0">
                        <td className="py-2 px-2">
                          <div>{row.studentName}</div>
                          <div className="text-xs text-muted-foreground">Grade {row.grade}</div>
                        </td>
                        <td className="py-2 px-2 text-muted-foreground">{row.parentName}</td>
                        <td className="py-2 px-2 font-mono text-xs">{row.parentPhone}</td>
                        <td className="py-2 px-2 text-muted-foreground whitespace-nowrap">
                          {row.lastReportSent
                            ? new Date(row.lastReportSent).toLocaleDateString("en-JM", { timeZone: "America/Jamaica", month: "short", day: "numeric", hour: "numeric", minute: "2-digit", hour12: true })
                            : "Never"}
                        </td>
                        <td className="py-2 px-2">
                          <Switch
                            checked={true}
                            onCheckedChange={(checked) => weeklyToggleOptInMutation.mutate({ userId: row.userId, optIn: checked })}
                            disabled={weeklyToggleOptInMutation.isPending}
                          />
                        </td>
                        <td className="py-2 px-2">
                          <div className="flex gap-1.5">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => { setWeeklyPreviewUserId(row.userId); weeklyPreviewMutation.mutate({ userId: row.userId }); }}
                              disabled={weeklyPreviewMutation.isPending}
                            >
                              <Eye className="w-3.5 h-3.5 mr-1" />
                              Preview
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => weeklySendOneMutation.mutate(row.userId)}
                              disabled={weeklySendOneMutation.isPending}
                            >
                              <Send className="w-3.5 h-3.5 mr-1" />
                              Send
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Preview Dialog */}
      <Dialog open={weeklyPreviewOpen} onOpenChange={setWeeklyPreviewOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Smartphone className="w-4 h-4 text-primary" />
              Message Preview
            </DialogTitle>
          </DialogHeader>
          {weeklyPreview && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-muted-foreground text-xs">Sending to</p>
                  <p className="font-mono">{weeklyPreview.parentPhone}</p>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs">Parent name</p>
                  <p>{weeklyPreview.parentName || "–"}</p>
                </div>
              </div>
              <div className="rounded-md bg-muted/60 p-4 text-sm whitespace-pre-line font-mono leading-relaxed">
                {weeklyPreview.message}
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setWeeklyPreviewOpen(false)}>Close</Button>
                <Button
                  onClick={() => {
                    weeklySendOneMutation.mutate(weeklyPreviewUserId);
                    setWeeklyPreviewOpen(false);
                  }}
                  disabled={weeklySendOneMutation.isPending}
                >
                  <Send className="w-3.5 h-3.5 mr-1.5" />
                  Send Now
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
